import { useCallback, useEffect, useState } from "react";
import { api, type ChannelStream, type Device, type LiveGpsUpdate } from "../api";
import { usePushToTalk } from "../hooks/usePushToTalk";
import { useLiveListen } from "../hooks/useLiveListen";
import { formatVoiceStats } from "../lib/audio-debug";
import { formatKathmanduClock } from "../lib/nepal-time";
import { logLiveStep } from "../lib/live-trace";
import { cameraCardStatus, gpsPoint, gpsUiStatus } from "../lib/presence";
import { VideoPlayer, type PlayerStatus } from "./VideoPlayer";

export { talkWsUrl } from "../hooks/usePushToTalk";

function SpeakerIcon({ muted }: { muted: boolean }) {
  return muted ? (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="currentColor" d="M5 10v4h3l4 4V6L8 10H5z" />
      <path fill="currentColor" d="M15.2 9.1 16.6 7.7 20.3 11.4 22 9.7 23.4 11.1 21.7 12.8 23.4 14.5 22 15.9 20.3 14.2 16.6 17.9 15.2 16.5 18.9 12.8z" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="currentColor" d="M5 10v4h3l4 4V6L8 10H5zm11.5 2a3.5 3.5 0 0 0-1.8-3.1v6.2A3.5 3.5 0 0 0 16.5 12zm0-7.3v2.1A6.5 6.5 0 0 1 20 12a6.5 6.5 0 0 1-3.5 5.2v2.1A8.5 8.5 0 0 0 22 12a8.5 8.5 0 0 0-5.5-7.3z" />
    </svg>
  );
}

function RouteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16.5c2.2-1.2 3.4-3.4 5.2-3.4 2 0 2.4 2.2 4.6 2.2 1.6 0 2.6-1.4 4.2-2.4"
      />
      <circle cx="6.2" cy="16.2" r="1.6" fill="currentColor" />
      <path fill="currentColor" d="M17.2 6.2 20.6 12H13.8L17.2 6.2Z" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="currentColor" d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.9V21h2v-3.1A7 7 0 0 0 19 11h-2z" />
    </svg>
  );
}

export function DeviceCameraCard({
  device,
  live,
  delaySec,
  selected,
  onSelect,
  onEnsureChannel,
  onPlayChannel,
  onRetryChannel,
  gps,
  gpsSocketConnected,
  recoverNonce = 0,
  pageActive = true,
  onPlayback,
  liveTrip = false,
  onToggleLiveTrip,
}: {
  device: Device;
  live: ChannelStream[];
  delaySec: number;
  selected: boolean;
  onSelect: () => void;
  onEnsureChannel?: (channel: number) => void;
  onPlayChannel?: (channel: number) => void;
  onRetryChannel?: (channel: number) => void;
  gps?: LiveGpsUpdate | null;
  gpsSocketConnected: boolean;
  recoverNonce?: number;
  pageActive?: boolean;
  onPlayback?: () => void;
  liveTrip?: boolean;
  onToggleLiveTrip?: () => void;
}) {
  const [muted, setMuted] = useState(true);
  const [statuses, setStatuses] = useState<Record<number, PlayerStatus>>({});
  const [talkReady, setTalkReady] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [, setGpsTick] = useState(0);
  const deviceName = device.label || device.serial_no;
  const anyPlayerLive = live.some((c) => statuses[c.channel_no] === "live");
  const anyPlayerConnecting = live.some((c) => statuses[c.channel_no] === "connecting" && !c.liveFailed);
  const anyChannelFailed = live.some((c) => c.liveFailed);
  const liveCount = live.filter((c) => c.live).length;
  const headerStatus = cameraCardStatus({
    deviceOnline: device.online,
    liveChannelCount: liveCount,
    anyPlayerLive,
    anyPlayerConnecting,
    anyChannelFailed,
  });
  const talkChannel = live[0]?.channel_no ?? 1;
  // 0 = auto-lock onto whichever JT1078 channel carries G.711 (often ch2).
  const listenChannel = 0;
  const streamLive = live.some((c) => c.live) || anyPlayerLive;
  const canAttemptTalk = device.online && streamLive && pageActive;
  const point = gpsPoint(device, gps);
  const gpsStatus = gpsUiStatus({
    deviceOnline: device.online,
    socketConnected: gpsSocketConnected,
    recordedAt: point?.freshnessAt ?? point?.recordedAt,
    liveFix: point?.liveFix,
    fixReport: point?.fixReport,
  });

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast((cur) => (cur === msg ? null : cur)), 4000);
  }, []);

  const { talking, buttonRef, pttHandlers } = usePushToTalk({
    deviceId: device.id,
    channel: talkChannel,
    online: device.online,
    hasLive: streamLive && pageActive,
    talkReady: (talkReady || canAttemptTalk) && pageActive,
    showToast,
  });

  useEffect(() => {
    if (!device.online) {
      setTalkReady(false);
      return;
    }
    let cancelled = false;
    let inflight = false;
    const load = async () => {
      if (inflight) return;
      inflight = true;
      try {
        const began = Date.now();
        const info = await api<{ available: boolean }>(`/api/devices/${device.id}/talk`);
        logLiveStep({
          vehicleId: device.id,
          channel: talkChannel,
          step: "talk_api",
          durationMs: Date.now() - began,
          error: info.available ? undefined : "unavailable",
        });
        if (!cancelled) setTalkReady(Boolean(info.available));
      } catch (err) {
        logLiveStep({
          vehicleId: device.id,
          channel: talkChannel,
          step: "talk_api",
          error: err instanceof Error ? err.message : "request_failed",
        });
        if (!cancelled) setTalkReady(false);
      } finally {
        inflight = false;
      }
    };
    void load();
    const t = window.setInterval(() => void load(), talkReady ? 20000 : 5000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [device.id, device.online, talkReady]);

  useEffect(() => {
    const t = window.setInterval(() => setGpsTick((n) => n + 1), 2_000);
    return () => window.clearInterval(t);
  }, []);

  const panes = live;
  const voiceReady = canAttemptTalk;
  const [streamAudio, setStreamAudio] = useState(false);
  // Speech rides the HLS AAC track (same ~10s timeline as the picture).
  // The side-channel G.711 player only runs when that track is absent.
  const { unlock, stats } = useLiveListen({
    deviceId: device.id,
    channel: listenChannel,
    enabled: voiceReady && !streamAudio,
    muted,
    paused: talking || !pageActive,
  });
  const pttBlocked = !canAttemptTalk;
  const anyRecovering = live.some((c) => c.runState === "recovering");
  const headerLabel =
    headerStatus === "live"
      ? "LIVE"
      : headerStatus === "offline"
        ? "OFFLINE"
        : headerStatus === "failed"
          ? "FAILED"
          : anyRecovering
            ? "RECOVERING"
            : live.some((c) => c.runState === "queued")
              ? "QUEUED"
              : "STARTING";
  const headerDot = headerStatus === "live" ? "ok" : headerStatus === "connecting" || headerStatus === "waiting" ? "warn" : "bad";
  const gpsDot = gpsStatus === "live" ? "ok" : gpsStatus === "reconnecting" || gpsStatus === "last_known" ? "warn" : "bad";
  const gpsLabel =
    gpsStatus === "live"
      ? "GPS FIX"
      : gpsStatus === "reconnecting"
        ? "GPS reconnecting..."
        : gpsStatus === "last_known"
          ? "LAST KNOWN"
          : "NO GPS FIX";
  const locLabel = point ? (point.liveFix && device.online ? "Current location" : "Last known location") : "No GPS fix yet";

  const toggleMute = () => {
    if (muted) unlock();
    setMuted((m) => !m);
  };

  return (
    <article
      className={`device-card dual ${selected ? "selected" : ""}`}
      onClick={onSelect}
    >
      <header className="device-card-head">
        <div>
          <strong>{deviceName}</strong>
          <span className="device-card-sub">Vehicle cameras</span>
        </div>
        <span className={`status-chip ${headerDot}`}>
          <span className={`status-dot ${headerDot}`} />
          {headerLabel}
        </span>
      </header>
      <div className="device-card-cams count-2">
        {panes.map((channel, index) => {
          const channelNo = channel?.channel_no ?? index + 1;
          const key = `${device.id}:${channelNo}`;
          const label =
            channelNo === 1 ? "Front Camera" : channelNo === 2 ? "Rear Camera" : `Camera ${channelNo}`;
          return (
            <VideoPlayer
              key={key}
              deviceLabel={label}
              stream={channel}
              deviceOnline={device.online}
              traceKey={`${device.id}:${channelNo}`}
              selected={selected}
              delaySec={delaySec}
              embedded
              muted={muted || !streamAudio}
              recoverNonce={recoverNonce}
              onAudioAvailable={(ok) => {
                if (ok) setStreamAudio(true);
              }}
              pageActive={pageActive}
              onRetry={device.online ? () => onRetryChannel?.(channelNo) : undefined}
              onPlay={channel?.playHeld ? () => onPlayChannel?.(channelNo) : undefined}
              onSelect={() => {
                onSelect();
              }}
              onStatusChange={(s) => {
                setStatuses((prev) => (prev[channelNo] === s ? prev : { ...prev, [channelNo]: s }));
              }}
            />
          );
        })}
      </div>
      <footer className="device-card-foot" onClick={(e) => e.stopPropagation()}>
        <div className="card-ctrls">
          <button
            type="button"
            className={`mute-btn is-single ${muted || !voiceReady ? "is-muted" : "is-on"}`}
            disabled={!voiceReady}
            title={!voiceReady ? "Camera is offline" : muted ? "Unmute camera audio" : "Mute camera audio"}
            aria-label={!voiceReady ? "Camera is offline" : muted ? "Unmute camera audio" : "Mute camera audio"}
            aria-pressed={!muted && voiceReady}
            onClick={(e) => {
              e.stopPropagation();
              toggleMute();
            }}
          >
            <SpeakerIcon muted={muted || !voiceReady} />
            <span className="mute-btn-label">{muted ? "Unmute" : "Mute"}</span>
          </button>
          {onPlayback ? (
            <button
              type="button"
              className="ctrl-btn playback-open-btn"
              title="Recorded playback"
              onClick={(e) => {
                e.stopPropagation();
                onPlayback();
              }}
            >
              Recorded playback
            </button>
          ) : null}
          {onToggleLiveTrip ? (
            <button
              type="button"
              className={`ctrl-btn live-trip-btn${liveTrip ? " is-on" : ""}`}
              aria-pressed={liveTrip}
              title={liveTrip ? "Close live trip" : "Open live trip for this vehicle"}
              onClick={(e) => {
                e.stopPropagation();
                onToggleLiveTrip();
              }}
            >
              <RouteIcon />
              <span>{liveTrip ? "Following" : "Live Trip"}</span>
            </button>
          ) : null}
          <button
            ref={buttonRef}
            type="button"
            className={`ctrl-btn ptt-btn ${talking ? "is-talking is-pressed" : ""} ${pttBlocked ? "is-blocked" : ""}`}
            aria-disabled={pttBlocked}
            title={talking ? "Talking… release to stop" : "Tap to Talk — hold to transmit"}
            aria-label="Tap to Talk"
            aria-pressed={talking}
            draggable={false}
            style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
            {...pttHandlers}
          >
            <MicIcon />
            <span className="ptt-copy">
              <span className="ptt-copy-idle" aria-hidden={talking}>
                Tap to Talk
              </span>
              <span className="ptt-copy-live" aria-hidden={!talking}>
                Talking…
              </span>
            </span>
          </button>
        </div>
        {toast ? <span className="card-toast" role="status">{toast}</span> : null}
        {stats ? <pre className="audio-debug" aria-hidden="true">{formatVoiceStats(stats)}</pre> : null}
        <div className="card-gps">
          <span className={`status-chip ${gpsDot}`}>
            <span className={`status-dot ${gpsDot}`} />
            {gpsLabel}
          </span>
          <span className="card-gps-loc">
            {point
              ? `${locLabel} · ${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}${
                  point.recordedAt ? ` · ${formatKathmanduClock(point.recordedAt)}` : ""
                }`
              : locLabel}
          </span>
        </div>
      </footer>
    </article>
  );
}
